/**
 * Which fog-of-war policy the World Map uses, and why (2026-10-30). The fog
 * itself is worldMapFog.ts; this module only decides when it is on.
 *
 * Reasons, strongest first: a live GM event, then the season, then a dev test
 * override. With no reason the fog is off, which is the default everywhere, so
 * shipping this changes nothing until a reason is added to a table below.
 * A reason can also turn the fog off ('off'), e.g. an event that reveals the
 * whole map while a fog season runs.
 */
import { isDevMode } from '../../../../shared/utils/isDevMode';
import { bootSearch } from '../../../../config/boot/bootUrl';
import type { WorldMapFogPolicy } from './worldMapFog';

export const WORLD_MAP_FOG_PRESETS = {
  /** Charted places plus rumours one road out: the everyday exploring fog. */
  explorer: { id: 'explorer', reveal: ['visited', 'current', 'roads'] },
  /** Only what the hero has walked: no rumours, for hardcore seasons or events. */
  uncharted: { id: 'uncharted', reveal: ['visited', 'current'] },
} as const satisfies Record<string, WorldMapFogPolicy>;

export type WorldMapFogPresetId = keyof typeof WORLD_MAP_FOG_PRESETS;
type FogChoice = WorldMapFogPresetId | 'off';

/** GM event templates (GmEventState.templateId) that change the fog while live. None yet. */
export const WORLD_MAP_FOG_BY_EVENT: Readonly<Record<string, FogChoice>> = {};
/** Seasons that change the fog. None yet; a season system supplies the id. */
export const WORLD_MAP_FOG_BY_SEASON: Readonly<Record<string, FogChoice>> = {};

export interface WorldMapFogContext {
  /** The live GM event's template id, when one is running. */
  readonly eventTemplateId?: string | null;
  readonly seasonId?: string | null;
  /** `?worldFog=explorer|uncharted|off` on a dev build, for testing. */
  readonly devOverride?: string | null;
}

export interface ResolvedWorldMapFog {
  readonly policy: WorldMapFogPolicy | null;
  readonly reason: 'event' | 'season' | 'dev' | 'default';
}

function choose(choice: string | null | undefined): WorldMapFogPolicy | null | undefined {
  if (choice === 'off') return null;
  return choice && Object.prototype.hasOwnProperty.call(WORLD_MAP_FOG_PRESETS, choice)
    ? WORLD_MAP_FOG_PRESETS[choice as WorldMapFogPresetId] : undefined;
}

export function resolveWorldMapFog(context: WorldMapFogContext): ResolvedWorldMapFog {
  const sources: [ResolvedWorldMapFog['reason'], string | null | undefined][] = [
    ['event', context.eventTemplateId ? WORLD_MAP_FOG_BY_EVENT[context.eventTemplateId] : undefined],
    ['season', context.seasonId ? WORLD_MAP_FOG_BY_SEASON[context.seasonId] : undefined],
    ['dev', context.devOverride],
  ];
  for (const [reason, choice] of sources) {
    const policy = choose(choice);
    if (policy !== undefined) return { policy, reason };
  }
  return { policy: null, reason: 'default' };
}

/** The dev override from the page URL (carried across portal travel), only on dev builds. */
export function readDevFogOverride(search: string = bootSearch()): string | null {
  return isDevMode() ? new URLSearchParams(search).get('worldFog') : null;
}
